import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { positions as api } from "@/lib/api/positions";
import type { PositionInput } from "@/lib/api/positions";
import { useUserChannel } from "@/hooks/useUserChannel";
import type { Position, PositionStatus } from "@/types/api";

export type PositionsStatus = "idle" | "loading" | "ready" | "error";

export interface PositionsFilter {
  status?: PositionStatus;
  ticker?: string;
}

export interface PositionsContextValue {
  status: PositionsStatus;
  positions: readonly Position[];
  archivedPositions: readonly Position[];
  hiddenPositions: readonly Position[];
  allPositions: readonly Position[];
  statisticalPositions: readonly Position[];
  error: Error | null;
  reload: (filter?: PositionsFilter) => Promise<void>;
  create: (input: PositionInput) => Promise<Position>;
  update: (id: string, patch: PositionInput) => Promise<Position>;
  archive: (id: string) => Promise<Position>;
  hide: (id: string) => Promise<Position>;
  restore: (id: string) => Promise<Position>;
  detachStrategy: (strategyId: string) => void;
  remove: (id: string) => Promise<void>;
}

const PositionsContext = createContext<PositionsContextValue>(null!);

export function usePositions(): PositionsContextValue {
  return useContext(PositionsContext);
}

function usePositionsProvider(): PositionsContextValue {
  const { subscribe } = useUserChannel();
  const [status, setStatus] = useState<PositionsStatus>("idle");
  const [positions, setPositions] = useState<readonly Position[]>([]);
  const [archivedPositions, setArchivedPositions] = useState<
    readonly Position[]
  >([]);
  const [hiddenPositions, setHiddenPositions] = useState<readonly Position[]>(
    [],
  );
  const [error, setError] = useState<Error | null>(null);

  const applyPosition = useCallback((position: Position) => {
    const upsert = (items: readonly Position[], include: boolean) => {
      const existing = items.some((item) => item.id === position.id);
      if (!include) return items.filter((item) => item.id !== position.id);
      return existing
        ? items.map((item) => (item.id === position.id ? position : item))
        : [position, ...items];
    };
    const hidden = position.hidden_at != null;
    const archived = !hidden && position.archived_at != null;
    setPositions((items) => upsert(items, !hidden && !archived));
    setArchivedPositions((items) => upsert(items, archived));
    setHiddenPositions((items) => upsert(items, hidden));
  }, []);

  const removePosition = useCallback((id: string) => {
    const without = (items: readonly Position[]) =>
      items.filter((position) => position.id !== id);
    setPositions(without);
    setArchivedPositions(without);
    setHiddenPositions(without);
  }, []);

  const reload = useCallback((filter?: PositionsFilter) => {
    setStatus("loading");
    setError(null);
    return Promise.all([
      api.listPositions({ ...filter, collection: "visible" }),
      api.listPositions({ ...filter, collection: "archived" }),
      api.listPositions({ ...filter, collection: "hidden" }),
    ])
      .then(([visible, archived, hidden]) => {
        setPositions(visible.positions);
        setArchivedPositions(archived.positions);
        setHiddenPositions(hidden.positions);
        setStatus("ready");
      })
      .catch((e: Error) => {
        setError(e);
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    queueMicrotask(() => void reload());
  }, [reload]);

  useEffect(
    () =>
      subscribe((message) => {
        if (message.event === "position.upserted") {
          const position = message.position;
          if (
            typeof position === "object" &&
            position != null &&
            "id" in position &&
            typeof position.id === "string"
          ) {
            applyPosition(position as Position);
          }
          return;
        }
        if (
          message.event === "position.removed" &&
          typeof message.position_id === "string"
        ) {
          removePosition(message.position_id);
        }
      }),
    [applyPosition, removePosition, subscribe],
  );

  const create = useCallback(
    async (input: PositionInput) => {
      const { position } = await api.createPosition(input);
      applyPosition(position);
      return position;
    },
    [applyPosition],
  );

  const update = useCallback(
    async (id: string, patch: PositionInput) => {
      const { position } = await api.updatePosition(id, patch);
      applyPosition(position);
      return position;
    },
    [applyPosition],
  );

  const archive = useCallback(
    async (id: string) => {
      const { position } = await api.archivePosition(id);
      applyPosition(position);
      return position;
    },
    [applyPosition],
  );

  const hide = useCallback(
    async (id: string) => {
      const { position } = await api.hidePosition(id);
      applyPosition(position);
      return position;
    },
    [applyPosition],
  );

  const restore = useCallback(
    async (id: string) => {
      const { position } = await api.restorePosition(id);
      applyPosition(position);
      return position;
    },
    [applyPosition],
  );

  const detachStrategy = useCallback((strategyId: string) => {
    const detach = (items: readonly Position[]) =>
      items.map((position) =>
        position.source_saved_strategy_id === strategyId
          ? { ...position, source_saved_strategy_id: null }
          : position,
      );
    setPositions(detach);
    setArchivedPositions(detach);
    setHiddenPositions(detach);
  }, []);

  const remove = useCallback(
    async (id: string) => {
      await api.deletePosition(id);
      removePosition(id);
    },
    [removePosition],
  );

  const statisticalPositions = useMemo(
    () => [...positions, ...archivedPositions],
    [archivedPositions, positions],
  );
  const allPositions = useMemo(
    () => [...positions, ...archivedPositions, ...hiddenPositions],
    [archivedPositions, hiddenPositions, positions],
  );

  return useMemo(
    () => ({
      status,
      positions,
      archivedPositions,
      hiddenPositions,
      allPositions,
      statisticalPositions,
      error,
      reload,
      create,
      update,
      archive,
      hide,
      restore,
      detachStrategy,
      remove,
    }),
    [
      status,
      positions,
      archivedPositions,
      hiddenPositions,
      allPositions,
      statisticalPositions,
      error,
      reload,
      create,
      update,
      archive,
      hide,
      restore,
      detachStrategy,
      remove,
    ],
  );
}

export function PositionsProvider({ children }: { children: ReactNode }) {
  const value = usePositionsProvider();
  return (
    <PositionsContext.Provider value={value}>
      {children}
    </PositionsContext.Provider>
  );
}
